import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Camera, User, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ImageSolverModal } from '@/components/ImageSolverModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLORS = ['#dd5f7a', '#2e7d4f', '#e8834a', '#2a9fe0', '#6b4fd8', '#dd5f7a', '#2e7d4f', '#e8834a', '#2a9fe0'];

const GradesPage = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [solverOpen, setSolverOpen] = useState(false);
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/grades`, { headers: getAuthHeaders() });
        setGrades(response.data);
      } catch (error) {
        toast.error('فشل في تحميل الصفوف');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchGrades();
  }, [getAuthHeaders]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userGrade = user?.grade_number != null ? Number(user.grade_number) : null;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="gradespage" dir="rtl" data-testid="grades-page">
      {/* Light header with authenticated user menu */}
      <header className="relative z-10 border-b border-black/5 bg-[#f6f2fc]/85 backdrop-blur-md" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/grades" className="flex items-center gap-3" data-testid="logo-link">
              <span className="text-xl font-bold text-[#2a2140] tracking-wide">RasheedGPT</span>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-[#2a2140]/80 hover:text-[#2a2140] hover:bg-black/5" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-[#dd5f7a] flex items-center justify-center text-white text-sm font-bold">{user?.name?.charAt(0)}</div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#2a2140]">
                <div className="px-3 py-2 border-b border-black/5">
                  <p className="font-semibold text-[#2a2140]">{user?.name}</p>
                  <p className="text-xs text-[#6a5f80]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
                </div>
                {user?.role === 'admin' && (
                  <DropdownMenuItem asChild className="text-[#38a0e8] hover:text-[#38a0e8] hover:bg-black/5 cursor-pointer">
                    <Link to="/admin" data-testid="admin-link"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
                  </DropdownMenuItem>
                )}
                {user?.role !== 'admin' && (
                  <DropdownMenuItem asChild className="text-[#2a2140]/80 hover:text-[#2a2140] hover:bg-black/5 cursor-pointer">
                    <Link to="/profile" data-testid="profile-link"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setSolverOpen(true)} className="text-[#2a2140]/80 hover:text-[#2a2140] hover:bg-black/5 cursor-pointer" data-testid="solve-question-btn">
                  <Camera className="w-4 h-4 me-2" />حل سؤال
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer" data-testid="logout-button">
                  <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Decorative rings */}
      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <span className="badge">اختر صفّك</span>
        <h1>الصفوف الدراسية</h1>
        <p className="lead">
          تذكّر دائماً حديث الرسول الكريم صلى الله عليه وسلم
          <b>"كلكم راعٍ وكلكم مسؤول عن رعيته"</b>
        </p>

        {loading ? (
          <div className="stairs" data-testid="grades-grid">
            {Array.from({ length: 9 }).map((_, idx) => (
              <div key={idx} className="g" style={{ '--k': idx, '--c': GRADE_COLORS[idx] }} aria-hidden="true">
                <span className="n">{idx + 1}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="stairs" data-testid="grades-grid">
            {grades.map((grade, idx) => {
              const color = GRADE_COLORS[(grade.number - 1) % GRADE_COLORS.length];
              const isOpen = isAdmin || !userGrade || Number(grade.number) === userGrade;
              const isYou = !isAdmin && !!userGrade && Number(grade.number) === userGrade;

              if (isOpen) {
                return (
                  <Link
                    to={`/grades/${grade.id}`}
                    key={grade.id}
                    className="g open"
                    style={{ '--k': idx, '--c': color }}
                    data-testid={`grade-card-${grade.number}`}
                  >
                    {isYou && <span className="you">أنت هنا يا بطل 👋</span>}
                    <span className="n">{grade.number}</span>
                    <b>{grade.name_ar}</b>
                    <small>{grade.name_en}</small>
                    <span className="go">ادخل ←</span>
                  </Link>
                );
              }

              return (
                <div
                  key={grade.id}
                  className="g"
                  style={{ '--k': idx, '--c': color }}
                  title="يُفتح لاحقاً"
                  aria-disabled="true"
                  data-testid={`grade-card-${grade.number}`}
                >
                  <span className="n">{grade.number}</span>
                  <b>{grade.name_ar}</b>
                  <small>{grade.name_en}</small>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="5" y="11" width="14" height="9" rx="2"></rect>
                    <path d="M8 11V8a4 4 0 018 0v3"></path>
                  </svg>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <ImageSolverModal open={solverOpen} onOpenChange={setSolverOpen} />
    </div>
  );
};

export default GradesPage;
